import { Button, logger, PipelineBadge } from '@/ui'
import { useAtomValue, useSetAtom } from 'jotai'
import { ArrowRightIcon, CalendarIcon, MailIcon, MessageCircleIcon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { clearSelectionAtom, selectedCountAtom } from './selection-atoms'

export interface BulkActionsBarProps {
	stages: Array<{ id: string; name: string; locked?: boolean }>
	sourceStages: string[]
	onBulkMove: (stageId: string) => void
	onBulkEmail?: () => void
	onBulkWhatsApp?: () => void
	onBulkSendBookingLink?: () => void
}

export function BulkActionsBar(
	{ stages, sourceStages, onBulkMove, onBulkEmail, onBulkWhatsApp, onBulkSendBookingLink }: BulkActionsBarProps,
) {
	const { t } = useTranslation('common')
	const selectedCount = useAtomValue(selectedCountAtom)
	const clearSelection = useSetAtom(clearSelectionAtom)
	const [moveOpen, setMoveOpen] = useState(false)

	const isVisible = selectedCount > 0

	const handleStageSelect = (stageId: string) => {
		setMoveOpen(false)
		logger.track('Candidates: Initiated Bulk Move', {
			data: {
				trigger: 'action_bar',
				candidate_count: selectedCount,
				source_stages: sourceStages,
			},
		})
		onBulkMove(stageId)
	}

	const handleEmail = () => {
		onBulkEmail?.()
		logger.track('Candidates: Initiated Bulk Email', { data: { candidate_count: selectedCount } })
	}

	const handleWhatsApp = () => {
		onBulkWhatsApp?.()
		logger.track('Candidates: Initiated Bulk WhatsApp', { data: { candidate_count: selectedCount } })
	}

	const handleSendBookingLink = () => {
		onBulkSendBookingLink?.()
		logger.track('Candidates: Initiated Bulk Booking Link', { data: { candidate_count: selectedCount } })
	}

	return (
		<AnimatePresence>
			{isVisible && (
				<motion.div
					initial={{ y: 80, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: 80, opacity: 0 }}
					transition={{ type: 'spring', damping: 25, stiffness: 300 }}
					className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40"
				>
					<div
						role="status"
						aria-live="polite"
						className="flex items-center gap-3 bg-white ring ring-grey-100 text-grey-800 rounded-lg shadow-lg px-3 py-2"
					>
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium">
								{t('bulkActions.selected', { count: selectedCount })}
							</span>
							<button
								type="button"
								onClick={() => clearSelection()}
								className="p-0.5 rounded text-grey-500 hover:text-grey-800 hover:bg-grey-100 transition-colors"
								aria-label={t('bulkActions.clearSelection')}
							>
								<XIcon className="size-4" />
							</button>
						</div>

						<div className="w-px h-6 bg-grey-100" />

						<div className="flex items-center gap-2">
							<div className="relative">
								{moveOpen && (
									<>
										<div
											className="fixed inset-0 z-0"
											onClick={() => setMoveOpen(false)}
											aria-hidden
										/>
										<div className="absolute bottom-full mb-2 left-0 z-10 w-56 max-h-72 overflow-y-auto rounded-lg border border-grey-100 bg-white shadow-xl p-1">
											{stages.map((stage) => (
												<button
													key={stage.id}
													type="button"
													onClick={() => handleStageSelect(stage.id)}
													className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-grey-50 transition-colors"
												>
													<PipelineBadge stage={stage.name.toLowerCase()} name={stage.name} />
												</button>
											))}
										</div>
									</>
								)}
								<Button type="committal" size="short" onClick={() => setMoveOpen((open) => !open)}>
									<ArrowRightIcon className="size-4 mr-1" />
									{t('bulkActions.moveToStage')}
								</Button>
							</div>

							{onBulkEmail && (
								<Button type="subtle" size="short" onClick={handleEmail}>
									<MailIcon className="size-4 mr-1" />
									{t('bulkActions.sendEmail')}
								</Button>
							)}

							{onBulkWhatsApp && (
								<Button type="subtle" size="short" onClick={handleWhatsApp}>
									<MessageCircleIcon className="size-4 mr-1" />
									{t('bulkActions.sendWhatsApp')}
								</Button>
							)}

							{onBulkSendBookingLink && (
								<Button type="subtle" size="short" onClick={handleSendBookingLink}>
									<CalendarIcon className="size-4 mr-1" />
									{t('bulkActions.sendBookingLink')}
								</Button>
							)}
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
